import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { Form } from "../../components/Form";
import { Input } from "../../components/Input";
import { SelectInput } from "../../components/SelectInput";
import { TextArea } from "../../components/TextArea";
import { spacing } from "@styling/sizing";
import { Country } from "@KassaroAB/shared/types/enums/Country";
import type {
  RestCreateSupplier,
  RestSupplier,
} from "@KassaroAB/shared/types/supplier";
import { FormSection } from "../../components/FormSection";

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const parseCountry = (value: string): Country | "" => {
  if (value === Country.FINLAND) return Country.FINLAND;
  if (value === Country.SWEDEN) return Country.SWEDEN;
  return "";
};

interface SupplierFormProps {
  formId: string;
  supplier?: RestSupplier;
  savedTick?: number;
  onSubmit: (data: RestCreateSupplier) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const SupplierForm = ({
  formId,
  supplier,
  savedTick,
  onSubmit,
  onDirtyChange,
}: SupplierFormProps) => {
  const { t } = useTranslation();

  const initialCountry: Country | "" = supplier?.country ?? "";

  const form = useForm({
    defaultValues: {
      name: supplier?.name ?? "",
      orgNumber: supplier?.orgNumber ?? "",
      contactPerson: supplier?.contactPerson ?? "",
      contactPhone: supplier?.contactPhone ?? "",
      contactEmail: supplier?.contactEmail ?? "",
      street: supplier?.street ?? "",
      city: supplier?.city ?? "",
      postalcode: supplier?.postalcode ?? "",
      country: initialCountry,
      note: supplier?.note ?? "",
    },
    onSubmit: ({ value }) => {
      const payload: RestCreateSupplier = {
        name: value.name,
      };
      if (value.orgNumber) payload.orgNumber = value.orgNumber;
      if (value.contactPerson) payload.contactPerson = value.contactPerson;
      if (value.contactPhone) payload.contactPhone = value.contactPhone;
      if (value.contactEmail) payload.contactEmail = value.contactEmail;
      if (value.street) payload.street = value.street;
      if (value.city) payload.city = value.city;
      if (value.postalcode) payload.postalcode = value.postalcode;
      if (value.country) payload.country = value.country;
      if (value.note) payload.note = value.note;
      onSubmit(payload);
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!savedTick) return;
    form.reset(form.state.values);
  }, [savedTick, form]);

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <FormSection title={t("supplierForm.basicInformation")}>
        <Fields>
          <form.Field name="name">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("supplierForm.name")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="orgNumber">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("supplierForm.orgNumber")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="contactPerson">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("supplierForm.contactPerson")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="contactPhone">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("supplierForm.contactPhone")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="contactEmail">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("supplierForm.contactEmail")}</FormLabel>
                <Input
                  type="email"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="street">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("supplierForm.street")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="postalcode">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("supplierForm.postalcode")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="city">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("supplierForm.city")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="country">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("supplierForm.country")}</FormLabel>
                <SelectInput
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(parseCountry(e.target.value))
                  }
                  onBlur={field.handleBlur}
                >
                  <option value="">{t("supplierForm.countryNone")}</option>
                  {Object.values(Country).map((country) => (
                    <option key={country} value={country}>
                      {t(`supplierForm.country${country}`)}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="note">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("supplierForm.note")}</FormLabel>
                <TextArea
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
        </Fields>
      </FormSection>
    </Form>
  );
};
